import { useState } from 'react';
import styled from 'styled-components';

import Alert from 'components/shared/Alert';
import Button from 'components/shared/Button';
import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import { getDefaultStyles } from 'components/shared/form/Select/customStyles';
import StandardModal, { Actions } from 'components/shared/StandardModal';
import { SyncPatientToEhrAction } from 'models/SyncPatientToEhrAction';
import Workflow, { WorkflowGroupOptions } from 'models/Workflow';
import { WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

import { platformLabel } from './SyncPatientBody';

interface Props {
  workflow: Workflow;
  onUpdateValues: (values: WorkflowValuesInput) => Promise<unknown>;
  // Controlled by the caller (a Menu.Item inside the outcome row's "⋯" menu) rather than owned
  // here: the trigger lives inside Menu.Content, which unmounts the instant the dropdown closes
  // (right after the item's own onClick), so state owned by this component would be torn down
  // before the modal it just opened could ever render.
  open: boolean;
  onClose: () => void;
}

// Matches the modal body rhythm the rest of this feature's confirm dialogs use (e.g.
// eligibility/RerunConfirmModal's Content) — StandardModal's own StyledContent padding has no gap
// between siblings, so without this wrapper the field, warnings, and Actions row all sat flush
// against each other instead of evenly spaced (OLIO-2846 QA: "looks different than normal").
const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 8px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Warning = styled(BodySmall)`
  color: ${colors.orangeHover};
`;

const SYNC_KIND = 'SyncPatientToEhr';

/**
 * Changes which facility a Selected referral was won for (OLIO-2846).
 *
 * It lives in the sync folder because the sync is the only reason it exists: the outcome result was
 * read-only, and a coordinator who moved a patient to a different building had no way to record it —
 * which meant no way to push the patient to the right EHR facility either.
 *
 * A plain VALUE WRITE, not an amend. `amend` cannot work here: run_action returns nil on a decided
 * run, so amending a terminal referral's action is a silent no-op. A MANUAL write of
 * selected_facility_id (rank 3) outranks AcceptReferral's ACTION value (rank 1), and write_values is
 * deliberately not terminal-gated, so this is the supported path. The API re-checks that the new
 * building is one the referral actually targets — same rule, same message as AcceptReferral's
 * completion-time guard.
 */
export default function ChangeSelectedFacility({ workflow, onUpdateValues, open, onClose }: Props) {
  const [saving, setSaving] = useState(false);
  const currentId = workflow.valueFor('selected_facility_id')?.value ?? '';
  const [facilityId, setFacilityId] = useState(currentId);

  // Only buildings this referral targets — mirroring the server guard, so the picker cannot offer
  // something the API will refuse.
  const facilities = workflow.groups;

  // A push that already LANDED somewhere. Its facility is what makes the warning below necessary and
  // specific: there is no un-sync API at either vendor, so the first record survives the change.
  //
  // ALL the siblings, not `find` — the sync is per-building now, so a run carries one instance per
  // targeted building and `find` returned whichever the API listed first. That is usually a `skipped`
  // loser, which reports nothing landed, so the "already synced to X, must be discharged there"
  // warning silently disappeared — at the exact moment a coordinator moves a patient, which is the
  // only moment it exists for.
  //
  // `isSyncedSuccessfully`, not `isCompleted` — duplicate/locked/matched/no_match also arrive completed
  // and name the facility they were ATTEMPTED against. Keying on completion told a coordinator a
  // record exists in a building where a duplicate had in fact created nothing. The getter is shared
  // with SyncPatientBody so the two cannot drift back apart.
  const syncs = workflow.actions.filter((action) => action.kind === SYNC_KIND) as SyncPatientToEhrAction[];
  const landed = syncs.find((action) => action.isSyncedSuccessfully);
  const syncedGroupId = landed?.syncedFacilityGroupId ?? null;
  const syncedName = facilities.find((group) => group.id === syncedGroupId)?.name;

  // A push that is IN FLIGHT right now — a distinct warning from the completed one, and the one a
  // coordinator is least likely to reason out for themselves. Changing the winner cannot recall a
  // vendor call already on its way: it will land at the OLD facility and create a record there, and
  // the result will come back and settle against this referral afterwards. Raised in review on
  // olio-api#3293 as a product note, because the API cannot fix it — nobody can un-send a SOAP call.
  // ANY sibling in flight, for the same reason: the push that cannot be recalled may belong to a
  // building other than whichever instance happened to come back first.
  const sendingNow = syncs.some((action) => action.isRunning);

  const save = async () => {
    setSaving(true);
    try {
      await onUpdateValues({ selected_facility_id: facilityId });
      onClose();
    } catch {
      // Stay open and swallow, the same shape as the sync modal's send. Without the catch a rejected
      // write (a 422 for a building the referral doesn't target) escaped as an unhandled rejection —
      // the toast still showed and the modal still stayed open, so the user saw the right thing, but
      // the noise would have buried a real failure in the console.
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <StandardModal title='Change Selected Facility' onCancel={onClose} maxWidth='560px'>
      <ModalBody>
        <Field>
          <FieldLabel name='change-facility'>Which facility did the patient select?</FieldLabel>
          <Select<WorkflowGroupOptions>
            inputId='change-facility'
            options={facilities}
            value={facilities.find((group) => group.id === facilityId) ?? null}
            onChange={(group) => setFacilityId(group?.id ?? '')}
            getOptionValue={(group) => group.id}
            getOptionLabel={(group) => group.name}
            placeholder='Select a facility'
            dataTestId='change-facility-select'
            menuPortalTarget={document.body}
            // The menu is portalled to <body> to escape the modal's clipping, and react-select
            // defaults a portalled menu to z-index 1 — below StandardModal's 901. Without this it
            // opened BEHIND the dialog: every facility was in the list, nothing was visible, and
            // the control read as disabled because clicking it appeared to do nothing.
            //
            // Styles are spread rather than replaced because a `styles` prop OVERRIDES the
            // component's own `styles={getDefaultStyles()}` (it is applied before the passthrough
            // spread), so passing only menuPortal here would drop the house look entirely.
            //
            // Local to this call site on purpose: Select's sibling MultiSelectChips already does
            // this internally, so the real fix is to make Select consistent with it — but that
            // touches every portalled Select in the app and belongs in its own PR, not this one.
            styles={{ ...getDefaultStyles(), menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </Field>

        {/* Always shown (OLIO-3148): changing the winner flips the previously-selected building to
            Not Selected, and the Figma calls for spelling that out so the coordinator isn't surprised
            by the old facility's status changing. Info styling (Alert), distinct from the orange
            un-sync warnings below. */}
        <Alert data-testid='change-facility-update-note'>
          <Alert.Text>
            <strong>Note</strong>: The previously ‘Selected’ facility will be updated to ‘Not Selected’
          </Alert.Text>
        </Alert>

        {/* Only shown when a push actually landed — an unsynced referral has nothing to warn about,
            and a warning that always appears is one nobody reads. Neither MatrixCare nor PCC has an
            un-sync API, so somebody has to discharge or reject the old record by hand. */}
        {sendingNow && (
          <Warning data-testid='change-facility-in-flight-warning'>
            This patient is being sent to {platformLabel(workflow.ehrSync.platform)} right now. Changing the winner will
            not stop it — that send will still create a record at the facility it was started for, which must then be
            discharged or rejected there.
          </Warning>
        )}

        {syncedName && (
          <Warning data-testid='change-facility-synced-warning'>
            This patient was already synced to {syncedName} in {platformLabel(workflow.ehrSync.platform)}. Changing the
            winner lets you sync to the new facility; the record in {syncedName} is not removed and must be discharged
            or rejected there.
          </Warning>
        )}

        <Actions>
          <Button variant='ghost' onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={saving || !facilityId || facilityId === currentId}
            onClick={save}
            data-testid='change-facility-confirm'>
            {saving ? 'Saving…' : 'Change Facility'}
          </Button>
        </Actions>
      </ModalBody>
    </StandardModal>
  );
}
